import React from 'react';

/**
 * Adaptador local con la firma de `InputTextPetra` (@ole/react-library-agents).
 *
 * Hoy renderiza el markup Aura: `[wrapper] > label.lbl + input.inp[.bad] + p.hint`.
 * Migración (OUW-72): reemplazar el `return` por `<InputTextPetra … />`.
 *
 * Divergencias respecto a `InputTextPetraProps` (docs/client-portal/DS-MIGRATION.md):
 * - `invalid`: marca `.bad` sin mensaje (el mock muestra el error en un alert aparte). En Petra
 *   el equivalente es `error` con texto; acá `error` también se acepta y se muestra como hint.
 * - `hint`: texto de ayuda bajo el campo. Petra no lo tiene.
 * - `name` / `autoComplete` / `inputMode` / `style`: passthrough al `<input>`.
 * - `wrapperClassName` / `wrapperStyle` / `data-spec`: van al contenedor (`.fieldset` en el gate).
 */
export type PortalInputProps = {
  id: string;
  type?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  error?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  testId?: string;
  // --- locales ---
  invalid?: boolean;
  hint?: string;
  name?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  style?: React.CSSProperties;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  'data-spec'?: string;
};

export function PortalInput({
  id,
  type = 'text',
  label,
  required,
  placeholder,
  value,
  disabled,
  error,
  onChange,
  testId,
  invalid,
  hint,
  name,
  autoComplete,
  inputMode,
  style,
  wrapperClassName,
  wrapperStyle,
  'data-spec': dataSpec,
}: PortalInputProps) {
  const bad = invalid || Boolean(error);
  return (
    <div className={wrapperClassName} style={wrapperStyle} data-spec={dataSpec}>
      {label && (
        <label className="lbl" htmlFor={id}>
          {label}
        </label>
      )}
      <input
        className={`inp${bad ? ' bad' : ''}`}
        id={id}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        autoComplete={autoComplete}
        inputMode={inputMode}
        style={style}
        data-testid={testId}
        onChange={onChange}
      />
      {error ? <p className="hint" role="alert">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}
